import React, { useState, useEffect } from 'react';
import {
  GECKO_CONSTS,
  GeckoToastService,
  GeckoFields,
  GECKO_UTILS,
  GeckoDialogService,
} from 'ui';
import FilePicker from 'components/FilePicker/FilePicker.jsx';
import ChannelSelect from 'components/ChannelSelect';
import AgentTeamSelect from 'components/AgentTeamSelect';
import ReplyBoxField from 'components/ReplyBoxField';
import GeckoLocation from 'services/gecko-location';
import ElementRadio from 'components/ElementRadio';
import GeckoRequest from 'services/gecko-request';
import { renderError } from 'services/gecko-error';
import {
  buildContactGroup,
  buildSelectedChannelGroup,
} from 'services/template-tags';
import { useUnsavedChanges } from 'components/UnsavedChanges';
import BotTabEditContainer from '../BotTabEditContainer';
import botMessages from '../../../constants/botMessages';
import { useBot } from '../hooks';
import { CONSTS } from 'constants/index';
import GeckoFeatures from 'services/gecko-features';
import GeckoStore from 'services/gecko-store';

import './index.scss';

function BotEdit({ botId }) {
  const [bot, setBot] = useState({
    name: '',
    supported_channels: [],
    availability: [],
    intro_message: '',
    miss_message: '',
    complaint_message: '',
    pass_to_human_message: '',
    pass_to_human: true,
    user: {
      first_name: '',
      last_name: '',
      additional: {},
    },
    configuration: {
      low_confidence_expiry_days: 0,
      no_match_expiry_days: 7,
      user_complaint_expiry_days: 30,
    },
    low_confidence: 40,
    high_confidence: 75,
    conversation_timeout: 28800,
  });
  const [showAdvancedSettings, setShowAdvancedSettings] = useState(false);
  const [loadedBot] = useBot(botId);
  const [clearUnsavedChanges, , , isDirty] = useUnsavedChanges([
    bot.name,
    bot.supported_channels,
    bot.availability,
    bot.intro_message,
    bot.miss_message,
    bot.complaint_message,
    bot.pass_to_human_message,
    bot.pass_to_human,
    bot.user,
    bot.configuration,
    bot.low_confidence,
    bot.high_confidence,
    bot.conversation_timeout,
  ]);

  useEffect(() => {
    if (loadedBot && !bot.id) {
      setBot(loadedBot);
    }
  }, [loadedBot]);

  // Clear unsaved changes when a bot loads in
  useEffect(() => {
    clearUnsavedChanges();
  }, [bot.id]);

  const detailsFields = [
    {
      key: 'name',
      label: 'Name',
      type: GECKO_CONSTS.FIELD_TYPE_TEXT,
      required: true,
    },
    {
      key: 'user.additional.profile_picture_url',
      label: 'Avatar',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      description: 'Recommended: 200px(w) x 200px(h) in JPG or PNG.',
      component: <FilePicker accept={CONSTS.IMAGE_ACCEPT_MIME_TYPES} />,
    },
  ];

  const templateContext = [
    buildContactGroup(),
    buildSelectedChannelGroup({
      channel: null,
      showChannelQueuePosition: true,
    }),
  ];
  const settingsFields = [
    {
      key: 'supported_channels',
      label: 'Only trigger on these Channels',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      component: <ChannelSelect multi={true} ignoredChannels={['mailgun']} />,
    },
    {
      key: 'availability',
      label: 'Only trigger when all these Agents are not available',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      component: <AgentTeamSelect multi={true} />,
    },
    bot.pass_to_human
      ? {
          key: 'assign_fallback',
          label: 'Assign Failed Conversation to',
          type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
          component: <AgentTeamSelect multi={true} />,
        }
      : {
          key: 'assign_fallback',
          label: '',
          type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
          component: <div style={{ visibility: 'hidden' }}></div>,
        },
    {
      key: 'configuration.enable_bot_emails',
      label: 'Enable monthly Bot stats email',
      type: GECKO_CONSTS.FIELD_TYPE_TOGGLE,
    },
    {
      key: 'pass_to_human',
      label: 'Pass to human',
      description: bot.pass_to_human
        ? 'If asked, your bot will hand over conversations to all Agents / Teams listed in the Assigned Failed Conversations To field.'
        : 'Your bot will never assign conversations to Agents / Teams',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      component: (
        <ElementRadio
          options={[
            {
              component: <button>Yes</button>,
              value: true,
            },
            {
              component: <button>No</button>,
              value: false,
            },
          ]}
        />
      ),
    },
  ];

  const botCustomMessageFields = [
    {
      key: '',
      label: 'Indicate how the bot should respond when:',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      width: '100%',
      component: <div style={{ display: 'none' }}></div>,
    },
    {
      key: 'intro_message',
      label: 'A user sends their first message in a conversation',
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      component: (
        <ReplyBoxField
          compileTemplateTags={false}
          templateContext={templateContext}
          meta={{ mail_merge: { show: true } }}
        />
      ),
    },
    {
      key: 'miss_message',
      label: "The bot doesn't know the answer to a question",
      placeholder: bot.pass_to_human
        ? botMessages.MISS
        : botMessages.MISS_PASS_TO_HUMAN_DISABLED,
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      component: (
        <ReplyBoxField
          compileTemplateTags={false}
          templateContext={templateContext}
          meta={{ mail_merge: { show: true } }}
        />
      ),
    },
    {
      key: 'complaint_message',
      label: 'The bot receives a complaint',
      placeholder: bot.pass_to_human
        ? botMessages.COMPLAINT
        : botMessages.COMPLAINT_PASS_TO_HUMAN_DISABLED,
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      component: (
        <ReplyBoxField
          compileTemplateTags={false}
          templateContext={templateContext}
          meta={{ mail_merge: { show: true } }}
        />
      ),
    },
    {
      key: 'pass_to_human_message',
      label: 'Asked to be passed to a human',
      placeholder: bot.pass_to_human
        ? botMessages.PASS_TO_HUMAN
        : botMessages.PASS_TO_HUMAN_DISABLED,
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      component: (
        <ReplyBoxField
          compileTemplateTags={false}
          templateContext={templateContext}
          meta={{ mail_merge: { show: true } }}
        />
      ),
    },
  ];

  const isSupport = GeckoStore.redux.get('appState.isSupport');
  const isChatGPT = bot.configuration.interface === 'chatgpt';
  const isHybrid = bot.configuration.interface === 'hybrid';
  const isWatson = bot.configuration.interface === 'watson-assistant';

  const advancedFields = [
    {
      key: 'high_confidence',
      label: 'Match breakpoint',
      description:
        'Set the minimum confidence level required for an answer to be handled as a match, A match will reply with the answer and not log anything in the training table.',
      placeholder: 75,
      width: '33%',
      required: true,
      type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
      min: bot.low_confidence,
      max: 100,
      decimalPlaces: 0,
      affix: '%',
      hideWhen: () => isChatGPT || isHybrid,
      disabled: !isSupport,
    },
    {
      key: 'low_confidence',
      label: 'Low confidence breakpoint',
      description:
        'Set the minimum confidence level required for an answer to be handled as a low confidence match. A low confidence match will reply with the answer and also log an entry in the training table.',
      placeholder: 40,
      width: '33%',
      required: true,
      type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
      min: 0,
      max: bot.high_confidence,
      decimalPlaces: 0,
      affix: '%',
      hideWhen: () => isChatGPT || isHybrid,
      disabled: !isSupport,
    },
    {
      key: 'conversation_timeout',
      required: true,
      label: 'Close conversations after period of inactivity',
      description:
        'Conversations the bot is handling will automatically be closed after the given period of inactivity',
      width: '33%',
      type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      options: [
        { label: '8 hours', value: 28800 },
        { label: '24 hours', value: 86400 },
        { label: '48 hours', value: 172800 },
        { label: '72 hours', value: 259200 },
      ],
      config: {
        clearable: false,
      },
    },
  ];

  const opportunityExpiryFields = [
    {
      key: 'configuration.low_confidence_expiry_days',
      description:
        'Set the number of days you want low confidence training opportunities to remain before automatically expiring (0 days will expire immediately).',
      label: 'Low Confidence',
      type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
      width: '33%',
      placeholder: 10,
      required: true,
      decimalPlaces: 0,
      affix: 'Days',
    },
    {
      key: 'configuration.no_match_expiry_days',
      description:
        'Set the number of days you want very low confidence training opportunities to remain before automatically expiring (0 days will expire immediately).',
      label: 'Very Low Confidence',
      type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
      width: '33%',
      placeholder: 10,
      required: true,
      decimalPlaces: 0,
      affix: 'Days',
    },
    {
      key: 'configuration.user_complaint_expiry_days',
      description:
        'Set the number of days you want user complaint training opportunities to remain before automatically expiring (0 days will expire immediately).',
      label: 'User Complaint',
      type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
      width: '33%',
      placeholder: 10,
      required: true,
      decimalPlaces: 0,
      affix: 'Days',
    },
  ];

  const hasChatGPTEnabled = isChatGPT || isHybrid;

  const aiEngineFields = [
    {
      key: 'configuration.interface',
      label: 'AI Engine',
      width: '33%',
      type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      options: [
        { label: 'IBM Watson', value: 'watson-assistant' },
        { label: 'ChatGPT', value: 'chatgpt' },
        { label: 'Hybrid (IBM + ChatGPT)', value: 'hybrid' },
      ],
      config: {
        clearable: false,
      },
    },
    hasChatGPTEnabled
      ? {
          key: 'configuration.site_id',
          label: 'Site ID',
          width: '33%',
          type: GECKO_CONSTS.FIELD_TYPE_NUMBER,
          decimalPlaces: 0,
        }
      : {
          key: 'assign_fallback',
          label: '',
          type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
          component: <div style={{ visibility: 'hidden' }}></div>,
        },
    hasChatGPTEnabled
      ? {
          key: 'configuration.model',
          label: 'ChatGPT Model',
          width: '33%',
          type: GECKO_CONSTS.FIELD_TYPE_SELECT,
          options: GeckoFeatures.can('gpt4')
            ? [
                { label: 'GPT-3.5', value: 'gpt-3.5-turbo' },
                { label: 'GPT-4', value: 'gpt-4' },
              ]
            : [{ label: 'GPT-3.5', value: 'gpt-3.5-turbo' }],
          config: {
            clearable: false,
          },
        }
      : {
          key: 'assign_fallback',
          label: '',
          type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
          component: <div style={{ visibility: 'hidden' }}></div>,
        },
  ];

  const handleChange = (key, value) => {
    const newBot = GECKO_UTILS.setNestedValue(bot, key, value);

    const confidenceMapping = {
      'watson-assistant': { low: 40, high: 75 },
      chatgpt: { low: 50, high: 75 },
      hybrid: { low: 50, high: 85 },
    };

    const confidenceValues = confidenceMapping[value];

    setBot({
      ...newBot,
      low_confidence: confidenceValues
        ? confidenceValues.low
        : newBot.low_confidence,
      high_confidence: confidenceValues
        ? confidenceValues.high
        : newBot.high_confidence,
    });
  };

  const saveBot = () => {
    if (botId === 'new') {
      return GeckoRequest.post('/bots', bot)
        .then((bot) => {
          clearUnsavedChanges();
          new GeckoToastService().success('New bot created successfully');
          return GeckoLocation.goTo(`/bots/${bot.id}/settings`);
        })
        .catch(renderError);
    }
    return GeckoRequest.post(`/bots/${botId}`, bot)
      .then((bot) => {
        clearUnsavedChanges();
        new GeckoToastService().success('Bot updated successfully');
      })
      .catch(renderError);
  };

  const cancelEdit = () => {
    if (botId && botId !== 'new') {
      GeckoLocation.goTo(`/bots/${botId}/train`);
    } else {
      GeckoLocation.goTo(`/bots`);
    }
  };

  const handleDelete = () => {
    return new GeckoDialogService()
      .confirm(`Are you sure you want to delete this Bot?`)
      .then((response) => {
        if (response) {
          return GeckoRequest.delete(`/bots/${botId}`).then(() => {
            new GeckoToastService().success(`This bot has been deleted`);
            clearUnsavedChanges();
            GeckoLocation.goTo('/bots');
          });
        }
        return false;
      })
      .catch(renderError);
  };

  const handleSupportClick = () => {
    window.Intercom('show');
  };

  return (
    <div className="BotEdit" key={bot.id}>
      <BotTabEditContainer
        title="Settings"
        saveText="Save Settings"
        onSave={saveBot}
        onCancel={cancelEdit}
        onDelete={botId !== 'new' ? handleDelete : undefined}
        disableSave={!isDirty}
      >
        <div class="BotEdit-settings-container col-12">
          <div>
            <h2>Bot Details</h2>
            <div className="row">
              <GeckoFields
                values={bot}
                handler={handleChange}
                fields={detailsFields}
              />
            </div>
          </div>
        </div>
        <div class="BotEdit-settings-container col-12">
          <div>
            <h2>General Settings</h2>
            <div className="row">
              <GeckoFields
                values={bot}
                handler={handleChange}
                fields={settingsFields}
              />
            </div>
          </div>
        </div>
        <div class="BotEdit-settings-container col-12">
          <div>
            <h2>Customisation Settings</h2>
            <div className="row">
              <GeckoFields
                values={bot}
                handler={handleChange}
                fields={botCustomMessageFields}
              />
            </div>
          </div>
        </div>
        <div class="BotEdit-settings-container col-12">
          <div>
            <h2
              className="BotEdit-advanced-toggle"
              onClick={() => setShowAdvancedSettings(!showAdvancedSettings)}
            >
              Show Advanced Settings{' '}
              <i
                className={`${
                  showAdvancedSettings ? 'far fa-angle-up' : 'far fa-angle-down'
                }`}
              ></i>
            </h2>
            {showAdvancedSettings ? (
              <div class="BotEdit-advanced-toggle-container">
                {GeckoFeatures.can('ai-engine') ? (
                  <>
                    <h2>Bot Settings</h2>
                    <div className="row">
                      <GeckoFields
                        values={bot}
                        handler={handleChange}
                        fields={aiEngineFields}
                      />
                    </div>
                    <hr />
                  </>
                ) : null}

                <h2>Bot Confidence</h2>
                <div className="row">
                  <GeckoFields
                    values={bot}
                    handler={handleChange}
                    fields={advancedFields}
                  />
                </div>
                {isWatson || bot.configuration.interface === undefined ? (
                  <p>
                    To change these breakpoints please{' '}
                    <span className="link" onClick={handleSupportClick}>
                      speak to support.
                    </span>
                  </p>
                ) : null}
                <hr />
                <h2>Bot Training Opportunity Expiry</h2>
                <div className="row">
                  <GeckoFields
                    values={bot}
                    handler={handleChange}
                    fields={opportunityExpiryFields}
                  />
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </BotTabEditContainer>
    </div>
  );
}

export default BotEdit;
